import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { CheckCircle } from "lucide-react";
import { handleError } from "@/lib/errorHandler";

export default function PublicForm() {
  const { shareToken } = useParams();
  const [form, setForm] = useState<any>(null);
  const [fields, setFields] = useState<any[]>([]);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchFormData();
  }, [shareToken]);

  const fetchFormData = async () => {
    try {
      const { data: formData, error: formError } = await supabase
        .from("forms")
        .select("*, tables(*)")
        .eq("share_token", shareToken)
        .eq("is_active", true)
        .single();

      if (formError) throw formError;
      setForm(formData);

      const { data: fieldsData, error: fieldsError } = await supabase
        .from("fields")
        .select("*")
        .eq("table_id", formData.table_id)
        .order("order_index");

      if (fieldsError) throw fieldsError;
      setFields(fieldsData);
    } catch (error: any) {
      handleError(error, "Form not found or inactive");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Server-side validation
      const { data: validation, error: validationError } = await supabase.functions.invoke(
        'validate-form-submission',
        {
          body: { fields, formData }
        }
      );

      if (validationError || !validation?.valid) {
        toast.error(validation?.errors?.join(', ') || 'Validation failed. Please check your inputs.');
        setSubmitting(false);
        return;
      }

      // Rate limiting check (server extracts IP automatically now)
      const { data: rateLimitData, error: rateLimitError } = await supabase.functions.invoke(
        'rate-limit-check',
        {
          body: {
            shareToken
          }
        }
      );

      if (rateLimitError || !rateLimitData?.allowed) {
        toast.error("Submission limit reached. Please try again later.");
        setSubmitting(false);
        return;
      }

      // Insert sanitized data into public_submissions table
      const { error } = await supabase
        .from("public_submissions")
        .insert([{
          form_id: shareToken,
          table_id: form.table_id,
          data: validation.sanitizedData,
          user_agent: navigator.userAgent,
          submitted_at: new Date().toISOString()
        }]);

      if (error) throw error;

      toast.success("Form submitted successfully!");
      setSubmitted(true);
      setFormData({});
    } catch (error: any) {
      handleError(error, "Unable to submit form. Please try again later");
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field: any) => {
    const value = formData[field.name] || "";
    const onChange = (val: any) => setFormData({ ...formData, [field.name]: val });

    switch (field.type) {
      case "long_text":
        return (
          <Textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            required={field.is_required}
            placeholder={`Enter ${field.name}`}
            maxLength={5000}
          />
        );
      case "number":
        return (
          <Input
            type="number"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            required={field.is_required}
            placeholder={`Enter ${field.name}`}
          />
        );
      case "email":
        return (
          <Input
            type="email"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            required={field.is_required}
            placeholder={`Enter ${field.name}`}
            maxLength={255}
          />
        );
      case "phone":
        return (
          <Input
            type="tel"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            required={field.is_required}
            placeholder={`Enter ${field.name}`}
            maxLength={20}
          />
        );
      case "date":
        return (
          <Input
            type="date"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            required={field.is_required}
          />
        );
      default:
        return (
          <Input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            required={field.is_required}
            placeholder={`Enter ${field.name}`}
            maxLength={500}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">Loading form...</div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="p-4 md:p-6">
            <CardTitle className="text-lg md:text-xl">Form Not Found</CardTitle>
            <CardDescription className="text-sm">This form is no longer available or has been deactivated.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center p-4 md:p-6">
            <div className="mx-auto mb-4 w-14 h-14 md:w-16 md:h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <CheckCircle className="h-7 w-7 md:h-8 md:w-8 text-green-600 dark:text-green-400" />
            </div>
            <CardTitle className="text-lg md:text-xl">Thank You!</CardTitle>
            <CardDescription className="text-sm">Your response has been submitted successfully.</CardDescription>
          </CardHeader>
          <CardContent className="text-center p-4 pt-0 md:p-6 md:pt-0">
            <Button onClick={() => { setSubmitted(false); setFormData({}); }} className="touch-target">
              Submit Another Response
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 md:py-12 px-4 bg-gradient-to-b from-background to-muted">
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader className="p-4 md:p-6">
            <CardTitle className="text-xl md:text-2xl">{form.name}</CardTitle>
            {form.description && <CardDescription className="text-sm">{form.description}</CardDescription>}
          </CardHeader>
          <CardContent className="p-4 pt-0 md:p-6 md:pt-0">
            <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
              {fields.map((field) => (
                <div key={field.id} className="space-y-2">
                  <Label htmlFor={field.name} className="text-sm md:text-base">
                    {field.name}
                    {field.is_required && <span className="text-destructive ml-1">*</span>}
                  </Label>
                  {renderField(field)}
                </div>
              ))}
              <Button type="submit" disabled={submitting} className="w-full h-11 touch-target">
                {submitting ? "Submitting..." : "Submit"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}